// ClassView — the Class tab: class choice (chosen chip / served pick button
// with served refusal prose + future-perks list) and the Moulder goblin
// roster (per-goblin cards: hp / fed / tool / skills / assignment status,
// assign popover, unassign, armed sacrifice). Rendered blindly from
// vsj.class_panel; the driver owns every verdict and number shown here.
// NOTE: class_panel.level is NOT rendered — that number is character level
// and lives in the stats tab.

import { clear, el, kbar, kbtn, tooltip } from '../kit';
import { sendBtn, sendEvent } from '../net';
import * as icons from '../icons';
import * as vsj from '../vsj';

export function classView(): { root: HTMLElement; refresh(changed: Set<string>): void } {
  const choiceBox = el('div', { style: 'display:flex;flex-direction:column;gap:6px' });
  const goblinBox = el('div', { style: 'display:flex;flex-direction:column;gap:6px' });
  const root = el(
    'div',
    { class: 'k-panel k-scroll', style: 'flex:1;min-height:0;padding:10px;display:flex;flex-direction:column;gap:12px;overflow-y:auto' },
    choiceBox,
    goblinBox,
  );

  // ---- assign popover ----
  const openAssignPopover = (anchor: HTMLElement, assignBtn: any, kinds: any, crafts: any) => {
    document.getElementById('assign-pop')?.remove();
    if (!Array.isArray(kinds)) kinds = []; // assign_kinds is always served
    if (!Array.isArray(crafts)) crafts = [];
    const entity = assignBtn?._req_args?.entity;
    const pop = el('div', {
      id: 'assign-pop',
      class: 'k-panel',
      style:
        'position:fixed;z-index:70;width:260px;max-height:60vh;overflow-y:auto;padding:10px;' +
        'display:flex;flex-direction:column;gap:4px;background:var(--bg-modal);border-color:var(--border-light)',
    });
    pop.append(el('div', { class: 'k-small k-faint', text: 'Put them to work' }));
    for (const kind of kinds)
      pop.append(
        el('button', {
          class: 'k-btn compact',
          text: String(kind)[0].toUpperCase() + String(kind).slice(1),
          onclick: () => {
            sendEvent('assign_goblin', { entity, kind: String(kind) });
            pop.remove();
          },
        }),
      );
    pop.append(el('div', { class: 'k-small k-faint', text: 'Crafts' }));
    if (!crafts.length)
      pop.append(el('div', { class: 'k-small k-faint', text: 'Leave materials on its tile to unlock crafts.' }));
    for (const c of crafts) {
      if (!c || typeof c !== 'object') continue;
      pop.append(
        el('button', {
          class: 'k-btn compact',
          text: String(c.name ?? c.recipe ?? '?'),
          onclick: () => {
            sendBtn(c.assign);
            pop.remove();
          },
        }),
      );
    }
    document.body.append(pop);
    const r = anchor.getBoundingClientRect();
    pop.style.left = `${Math.min(r.left, innerWidth - 280)}px`;
    pop.style.top = `${Math.min(r.bottom + 4, innerHeight - pop.offsetHeight - 8)}px`;
    const dismiss = (e: PointerEvent) => {
      if (!pop.contains(e.target as Node)) {
        pop.remove();
        document.removeEventListener('pointerdown', dismiss);
      }
    };
    setTimeout(() => document.addEventListener('pointerdown', dismiss), 0);
  };

  // ---- goblin card ----
  const skillName = (id: string): string =>
    String(vsj.info('skill_' + id)?.name ?? id[0].toUpperCase() + id.slice(1));

  const goblinCard = (gid: string, g: any): HTMLElement => {
    const card = el('div', {
      class: 'k-panel raised',
      style: 'flex:1 1 300px;max-width:420px;padding:10px;display:flex;flex-direction:column;gap:6px',
    });

    // header: name · where · what it's doing
    card.append(
      el(
        'div',
        { style: 'display:flex;gap:8px;align-items:baseline' },
        el('span', { class: 'k-title', text: String(g.name ?? gid) }),
        el('span', { class: 'k-small k-dim', text: `at ${g.at ?? '?'}` }),
        el('span', {
          class: 'k-small k-dim',
          style: 'flex:1;text-align:right;overflow:hidden;text-overflow:ellipsis;white-space:nowrap',
          text: String(g.doing ?? ''),
        }),
      ),
    );

    // hp bar + fed status
    const hpBar = kbar('hp', 10);
    hpBar.root.style.flex = '1';
    hpBar.set(Number(g.hp ?? 0) / Math.max(1, Number(g.hp_max ?? 1)));
    const fedChip = g.stopped
      ? el('span', { class: 'k-chip', style: 'color:var(--danger)', text: 'starving' })
      : !g.fed
        ? el('span', { class: 'k-chip', style: 'color:var(--warn)', text: 'no food here' })
        : el('span', { class: 'k-chip', style: 'color:var(--ok)', text: 'fed' });
    tooltip(fedChip, () => 'Goblins eat food left on the ground at their tile.');
    card.append(
      el(
        'div',
        { style: 'display:flex;gap:6px;align-items:center' },
        icons.texture('ui_hp', 14),
        hpBar.root,
        el('span', { class: 'k-small k-dim', text: `${g.hp ?? 0} / ${g.hp_max ?? 0}` }),
        fedChip,
      ),
    );

    // tool
    const toolRow = el('div', { style: 'display:flex;gap:6px;align-items:center' });
    const tool = g.tool;
    if (tool && typeof tool === 'object') {
      let ttext = vsj.infoName(String(tool.item ?? ''));
      if (tool.durability && typeof tool.durability === 'object')
        ttext += `  ${tool.durability.cur ?? 0}/${tool.durability.max ?? 0}`;
      toolRow.append(icons.texture(String(tool.item ?? ''), 16), el('span', { class: 'k-small k-dim', text: ttext }));
    } else toolRow.append(el('span', { class: 'k-small k-faint', text: 'no tool' }));
    card.append(toolRow);

    // earned skills
    const skills = g.skills && typeof g.skills === 'object' ? g.skills : {};
    const sids = Object.keys(skills);
    if (sids.length) {
      const chips = el('div', { style: 'display:flex;gap:4px;flex-wrap:wrap' });
      for (const id of sids)
        chips.append(el('span', { class: 'k-chip k-small', text: `${skillName(id)} ${skills[id]}` }));
      card.append(chips);
    }

    // actions
    const actions = el('div', { style: 'display:flex;gap:6px;align-items:center;flex-wrap:wrap' });
    if (g.assign && typeof g.assign === 'object') {
      const ab = el('button', { class: 'k-btn compact', text: 'Assign' });
      ab.onclick = () => openAssignPopover(ab, g.assign, g.assign_kinds, g.assign_crafts);
      actions.append(ab);
    }
    if (g.unassign) actions.append(kbtn(g.unassign, { label: 'Unassign', compact: true }));
    actions.append(el('span', { style: 'flex:1' }));
    if (g.sacrifice && typeof g.sacrifice === 'object') {
      let armed = false;
      const label = `Sacrifice +${g.sacrifice_xp ?? 0} xp`;
      const sb = el('button', { class: 'k-btn compact danger', text: label });
      tooltip(sb, () => 'Destroys the goblin for Moulding XP. Its tool drops to the ground.');
      sb.onclick = () => {
        if (!armed) {
          armed = true;
          sb.textContent = 'Sure?';
          setTimeout(() => {
            armed = false;
            sb.textContent = label;
          }, 2500);
          return;
        }
        sendBtn(g.sacrifice);
      };
      actions.append(sb);
    }
    if (actions.childElementCount > 1) card.append(actions);
    // served burn prose — faint line under the goblin
    if (g.burn_text != null) card.append(el('div', { class: 'k-small k-faint', text: String(g.burn_text) }));
    return card;
  };

  // ---- sections ----
  const refreshChoices = () => {
    clear(choiceBox);
    const cp = vsj.at('class_panel') ?? {};
    choiceBox.append(el('div', { class: 'k-small k-faint', text: 'Class' }));
    const chosen = cp.chosen;
    const choices = cp.choices && typeof cp.choices === 'object' ? cp.choices : {};
    const cards = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap' });
    choiceBox.append(cards);
    if (!Object.keys(choices).length) {
      cards.append(el('div', { class: 'k-small k-faint', text: 'Nothing here yet.' }));
      return;
    }
    for (const cls of Object.keys(choices)) {
      const ch = choices[cls];
      if (!ch || typeof ch !== 'object') continue;
      const card = el('div', {
        class: 'k-panel raised',
        style: 'flex:1 1 260px;max-width:420px;padding:10px;display:flex;flex-direction:column;gap:6px',
      });
      const head = el(
        'div',
        { style: 'display:flex;gap:8px;align-items:center' },
        el('span', { class: 'k-title', style: 'flex:1', text: cls[0].toUpperCase() + cls.slice(1) }),
      );
      if (String(chosen) === cls)
        head.append(el('span', { class: 'k-chip', style: 'border-color:var(--border-active)', text: 'chosen' }));
      else if (chosen == null)
        head.append(
          kbtn(ch.pick, {
            label: `Choose ${cls[0].toUpperCase()}${cls.slice(1)}`,
            // served refusal prose (the threshold is driver knowledge)
            denyReason: ch.pick_blocked != null ? String(ch.pick_blocked) : undefined,
          }),
        );
      card.append(head);
      if (Array.isArray(ch.future) && ch.future.length)
        card.append(
          el('div', { class: 'k-small k-faint', style: 'opacity:0.7', text: 'Later: ' + ch.future.join(', ') }),
        );
      cards.append(card);
    }
  };

  const refreshGoblins = () => {
    clear(goblinBox);
    const cp = vsj.at('class_panel') ?? {};
    const goblins = cp.goblins && typeof cp.goblins === 'object' ? cp.goblins : {};
    const gids = Object.keys(goblins).sort();
    const isMoulder = String(cp.chosen) === 'moulder';
    if (!isMoulder && !gids.length) {
      goblinBox.style.display = 'none';
      return;
    }
    goblinBox.style.display = 'flex';
    const m = cp.moulding && typeof cp.moulding === 'object' ? cp.moulding : {};
    const head = el(
      'div',
      { style: 'display:flex;gap:8px;align-items:baseline' },
      el('span', { class: 'k-small k-faint', style: 'flex:1', text: 'Goblins' }),
    );
    if (m.level != null) head.append(el('span', { class: 'k-small k-dim', text: `Moulding Lv ${m.level}` }));
    if (m.goblin_cap != null)
      head.append(el('span', { class: 'k-small k-dim', text: `${m.goblin_count ?? gids.length} / ${m.goblin_cap}` }));
    goblinBox.append(head);
    if (!gids.length) {
      goblinBox.append(el('div', { class: 'k-small k-faint', text: 'No goblins yet — mould one at a mudpit.' }));
      return;
    }
    const grid = el('div', { style: 'display:flex;gap:8px;flex-wrap:wrap;align-items:flex-start' });
    for (const gid of gids)
      if (goblins[gid] && typeof goblins[gid] === 'object') grid.append(goblinCard(gid, goblins[gid]));
    goblinBox.append(grid);
  };

  refreshChoices();
  refreshGoblins();

  return {
    root,
    refresh(changed) {
      const all = changed.has('*');
      if (all || changed.has('class_panel') || changed.has('infos')) {
        refreshChoices();
        refreshGoblins();
      }
    },
  };
}
